import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInCalendarDays, parseISO } from 'date-fns';

import {
  DeadlineBucket,
  EnablementRow,
  EnablementSortKey,
  EnablementStatus,
  deadlineBucket,
  filterEnablement,
  sortEnablement,
  summarizeEnablement,
} from '../../support/enablementRows';
import {
  QuarterBounds,
  fiscalQuarterLabel,
} from '../../support/fiscalQuarters';
import {
  fmtDate,
  fmtShort,
  relativeDay,
} from '../../support/dateLabels';
import { avatarFor } from '../../support/avatar';
import { pageWindow } from '../../support/pageWindow';
import './OpportunityActivity.scss';
import './EnablementTable.scss';

export const STATUS_META: Record<
  EnablementStatus,
  { label: string; color: string }
> = {
  completed: { label: 'Completed', color: '#3070e7' },
  in_progress: { label: 'In progress', color: '#16bdeb' },
  not_started: { label: 'Not started', color: '#9ca3af' },
};
const STATUSES: EnablementStatus[] = [
  'completed',
  'in_progress',
  'not_started',
];
const PAGE_SIZES = [5, 10, 25];

const COLUMNS: Array<{ key: EnablementSortKey; label: string }> = [
  { key: 'name', label: 'Item' },
  { key: 'required', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'percent', label: 'Progress' },
  { key: 'deadline', label: 'Due' },
];

type DeadlineTab = 'all' | 'overdue' | 'current' | 'next' | 'later' | 'none';

/** Which items the table lists: what must be done, what is optional, or both. */
type Scope = 'required' | 'optional' | 'all';
const SCOPES: Array<{ key: Scope; label: string }> = [
  { key: 'required', label: 'Required' },
  { key: 'optional', label: 'Optional' },
  { key: 'all', label: 'All' },
];

/** Opens an item in a new tab (Cyber Academy for journey maps, Mindtickle for series). */
export const openEnablement = (
  row: EnablementRow,
  navigate: (to: string) => void
) => {
  if (!row.href) return;
  if (row.external) window.open(row.href, '_blank', 'noopener,noreferrer');
  else navigate(row.href);
};

/** Under the title: the program, when it was assigned to you, and when a finished certificate lapses. */
const metaLine = (r: EnablementRow): string =>
  [
    r.detail,
    r.assignedOn ? `Assigned ${fmtDate(r.assignedOn)}` : '',
    r.status === 'completed' && r.expiresOn ? `Certificate expires ${fmtDate(r.expiresOn)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

/** "Due in 12 days", "Expires in 2 months", "3 days overdue". */
const deadlineNote = (r: EnablementRow): string => {
  if (!r.deadline) return '';
  const word = r.deadlineKind === 'expires' ? 'Expires' : 'Due';
  if (r.status === 'completed') return word;
  const n = differenceInCalendarDays(parseISO(r.deadline), new Date());
  if (n < 0) return `${-n} ${-n === 1 ? 'day' : 'days'} overdue`;
  return `${word} ${relativeDay(r.deadline).toLowerCase()}`;
};
const deadlineTone = (r: EnablementRow): string => {
  if (!r.deadline || r.status === 'completed') return '';
  const n = differenceInCalendarDays(parseISO(r.deadline), new Date());
  return n < 0 ? 'is-late' : n <= 30 ? 'is-soon' : '';
};

interface Props {
  rows: EnablementRow[];
  loading?: boolean;
  /** Fiscal quarters for the deadline filters; without them only "Overdue" and "Later" can be told apart. */
  bounds?: QuarterBounds | null;
  /** Shown when there are no rows at all (not when filters hide them). */
  emptyNote?: string;
  sample?: boolean;
}

const EnablementTable: React.FC<Props> = ({
  rows,
  loading,
  bounds = null,
  emptyNote,
  sample,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statuses, setStatuses] = useState<Set<EnablementStatus>>(new Set());
  const [tab, setTab] = useState<DeadlineTab>('all');
  const [scope, setScope] = useState<Scope>('required');
  const [sortKey, setSortKey] = useState<EnablementSortKey>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  const today = useMemo(() => new Date(), []);
  // The headline figures are about what has to be done, whichever items the table is showing.
  const summary = useMemo(
    () => summarizeEnablement(rows.filter((r) => r.required)),
    [rows]
  );
  const scoped = useMemo(
    () =>
      scope === 'all'
        ? rows
        : rows.filter((r) => r.required === (scope === 'required')),
    [rows, scope]
  );
  const scopeCounts = useMemo(() => {
    const matching = filterEnablement(rows, { query });
    return {
      required: matching.filter((r) => r.required).length,
      optional: matching.filter((r) => !r.required).length,
      all: matching.length,
    } as Record<Scope, number>;
  }, [rows, query]);

  // Each chip group counts against the other group's selection, so a selected chip never disappears.
  const byStatus = useMemo(
    () => filterEnablement(scoped, { query, statuses }),
    [scoped, query, statuses]
  );
  const selFilter = useMemo(
    () => (tab === 'all' ? {} : { deadline: tab, bounds, today }),
    [tab, bounds, today]
  );
  const byDeadline = useMemo(
    () => filterEnablement(scoped, { query, ...selFilter }),
    [scoped, query, selFilter]
  );
  const shown = useMemo(
    () => filterEnablement(scoped, { query, statuses, ...selFilter }),
    [scoped, query, statuses, selFilter]
  );
  const sorted = useMemo(
    () => sortEnablement(shown, sortKey, sortDir),
    [shown, sortKey, sortDir]
  );
  const columns = COLUMNS;

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(
    () => setPage(0),
    [query, statuses, tab, scope, sortKey, sortDir, pageSize]
  );
  const current = Math.min(page, pages - 1);
  const visible = sorted.slice(
    current * pageSize,
    current * pageSize + pageSize
  );
  const from = sorted.length ? current * pageSize + 1 : 0;
  const to = Math.min(sorted.length, (current + 1) * pageSize);
  const filtered = statuses.size > 0 || tab !== 'all' || query.trim() !== '';

  const sortBy = (key: EnablementSortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'percent' ? 'desc' : 'asc');
    }
  };
  const toggleStatus = (s: EnablementStatus) =>
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  // Deadline tabs, named for the fiscal quarters the deadlines fall in.
  const tabs = useMemo(() => {
    const count = (k: DeadlineBucket) =>
      byStatus.filter((r) => deadlineBucket(r, bounds, today) === k).length;
    const daysLeft = bounds?.current
      ? differenceInCalendarDays(parseISO(bounds.current.end), today)
      : 0;
    const last = (bounds?.next ?? bounds?.current)?.end ?? null;
    const list: Array<{
      key: DeadlineTab;
      label: string;
      sub: string;
      n: number;
      now?: boolean;
    }> = [
      { key: 'all', label: 'All', sub: 'Any deadline', n: byStatus.length },
      {
        key: 'overdue',
        label: 'Overdue',
        sub: 'Past due',
        n: count('overdue'),
      },
    ];
    if (bounds?.current) {
      list.push({
        key: 'current',
        label: fiscalQuarterLabel(bounds.current.start),
        sub: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`,
        n: count('current'),
        now: true,
      });
    }
    if (bounds?.next) {
      list.push({
        key: 'next',
        label: fiscalQuarterLabel(bounds.next.start),
        sub: `${fmtShort(bounds.next.start)} – ${fmtShort(bounds.next.end)}`,
        n: count('next'),
      });
    }
    list.push({
      key: 'later',
      label: 'Later',
      sub: last ? `After ${fmtShort(last)}` : 'Further out',
      n: count('later'),
    });
    list.push({
      key: 'none',
      label: 'No deadline',
      sub: 'Not scheduled',
      n: count('none'),
    });
    return list;
  }, [byStatus, bounds, today]);

  return (
    <section className='v2__section oa'>
      <div className='v2__wrap'>
        <header className='oa__head'>
          <div>
            <h3 className='oa__title'>Enablement</h3>
            <p className='oa__lede'>
              Your assigned enablement, with what you are required to complete
              up front.
              {sample && (
                <>
                  {' '}
                  <strong className='oa__demo'>Showing sample data.</strong>
                </>
              )}
            </p>
          </div>
        </header>

        <div className='oa__summary'>
          <dl className='oa__figures'>
            <div>
              <dt>Completed</dt>
              <dd>{loading ? '—' : `${summary.percent}%`}</dd>
            </div>
            <div>
              <dt>Remaining</dt>
              <dd>{loading ? '—' : summary.requiredOpen}</dd>
            </div>
          </dl>
          <div
            className='en__tabs en__tabs--inline'
            role='tablist'
            aria-label='Filter by deadline'
          >
            {tabs.map((t) => {
              const on = tab === t.key;
              const empty = t.n === 0 && !on && t.key !== 'all';
              return (
                <button
                  key={t.key}
                  type='button'
                  role='tab'
                  aria-selected={on}
                  disabled={empty}
                  className={`en__tab${on ? ' is-on' : ''}${empty ? ' is-empty' : ''}${t.key === 'overdue' && t.n > 0 ? ' is-late' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <span className='en__tab-top'>
                    <span className='en__tab-label'>{t.label}</span>
                    {t.now && <span className='en__tab-now'>Now</span>}
                    <span className='en__tab-n'>{t.n}</span>
                  </span>
                  <span className='en__tab-sub'>{t.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className='oa__card'>
          <div className='oa__tools'>
            <div className='en__filters'>
              <div className='oa__chips' role='group' aria-label='Show'>
                {SCOPES.map((sc) => (
                  <button
                    key={sc.key}
                    type='button'
                    aria-pressed={scope === sc.key}
                    className={scope === sc.key ? 'is-on' : ''}
                    style={
                      scope === sc.key
                        ? { borderColor: '#1a2332', background: '#f3f4f6' }
                        : undefined
                    }
                    onClick={() => setScope(sc.key)}
                  >
                    {sc.label}
                    <b>{scopeCounts[sc.key]}</b>
                  </button>
                ))}
              </div>
              <div
                className='oa__chips'
                role='group'
                aria-label='Filter by status'
              >
                {STATUSES.map((s) => {
                  const n = byDeadline.filter((r) => r.status === s).length;
                  if (n === 0 && !statuses.has(s)) return null;
                  return (
                    <button
                      key={s}
                      type='button'
                      aria-pressed={statuses.has(s)}
                      className={statuses.has(s) ? 'is-on' : ''}
                      style={
                        statuses.has(s)
                          ? {
                              borderColor: STATUS_META[s].color,
                              background: `${STATUS_META[s].color}1f`,
                            }
                          : undefined
                      }
                      onClick={() => toggleStatus(s)}
                    >
                      <i style={{ background: STATUS_META[s].color }} />
                      {STATUS_META[s].label}
                      <b>{n}</b>
                    </button>
                  );
                })}
                {statuses.size > 0 && (
                  <button
                    type='button'
                    className='oa__clear'
                    onClick={() => setStatuses(new Set())}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <input
              type='search'
              className='oa__search'
              placeholder='Search enablement'
              aria-label='Search enablement'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className='oa__table-wrap'>
            <table className='oa__table en__table'>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      scope='col'
                      aria-sort={
                        sortKey === c.key
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type='button'
                        onClick={() => sortBy(c.key)}
                        className={
                          sortKey === c.key ? `is-sorted is-${sortDir}` : ''
                        }
                      >
                        {c.label}
                      </button>
                    </th>
                  ))}
                  <th className='oa__go-col'>
                    <span className='oa__sr'>Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  [0, 1, 2, 3].map((i) => (
                    <tr key={i} className='oa__skeleton' aria-hidden='true'>
                      <td colSpan={columns.length + 1}>
                        <span />
                      </td>
                    </tr>
                  ))}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className='oa__state'>
                      {filtered
                        ? 'Nothing matches those filters.'
                        : emptyNote ||
                          (scope === 'required'
                            ? 'Nothing required has been assigned to you.'
                            : 'Nothing has been assigned to you.')}
                    </td>
                  </tr>
                )}
                {!loading &&
                  visible.map((r) => {
                    const done = r.status === 'completed';
                    const color = STATUS_META[r.status].color;
                    const av = avatarFor(r.name);
                    return (
                      <tr
                        key={r.id}
                        className={`oa__row${r.href ? '' : ' is-static'}`}
                        onClick={() => openEnablement(r, navigate)}
                      >
                        <td className='oa__opp en__item'>
                          <span
                            className='oa__avatar en__src'
                            style={{ background: av.bg }}
                            aria-hidden='true'
                          >
                            {av.initials}
                          </span>
                          <span className='oa__opp-text'>
                            {r.href ? (
                              <a
                                href={r.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openEnablement(r, navigate);
                                }}
                              >
                                {r.name}
                              </a>
                            ) : (
                              <strong>{r.name}</strong>
                            )}
                            {metaLine(r) && <span>{metaLine(r)}</span>}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`en__type${r.required ? ' is-req' : ''}`}
                          >
                            {r.required ? 'Required' : 'Optional'}
                          </span>
                        </td>
                        <td>
                          <span className='en__status'>
                            <i style={{ background: color }} />
                            {STATUS_META[r.status].label}
                          </span>
                        </td>
                        <td>
                          <span className='en__progress'>
                            <span className='en__track' aria-hidden='true'>
                              <i
                                style={{
                                  width: `${r.percent}%`,
                                  background: color,
                                }}
                              />
                            </span>
                            <b>{r.percent}%</b>
                          </span>
                          {r.progress && (
                            <small className='en__sub'>{r.progress}</small>
                          )}
                        </td>
                        <td>
                          {done && r.completedOn ? (
                            <span className='oa__two'>
                              {fmtDate(r.completedOn)}
                              <small>
                                Completed{' '}
                                {relativeDay(r.completedOn).toLowerCase()}
                              </small>
                            </span>
                          ) : r.deadline ? (
                            <span
                              className='oa__two'
                              title={r.dueSource === 'rule' ? "Worked out from the module's due-date rule" : undefined}
                            >
                              {fmtDate(r.deadline)}
                              <small className={deadlineTone(r)}>
                                {deadlineNote(r)}
                              </small>
                            </span>
                          ) : (
                            <span className='en__none'>
                              {done ? 'Completed' : 'No deadline'}
                            </span>
                          )}
                        </td>
                        <td className='oa__go-col' aria-hidden='true'>
                          {r.href && <span className='oa__go'>↗</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {!loading && sorted.length > 0 && (
            <footer className='oa__foot'>
              <span className='oa__range'>
                {from}–{to} of {sorted.length}
              </span>
              <div className='oa__pager'>
                <label>
                  Rows
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                {pages > 1 && (
                  <nav aria-label='Pagination'>
                    <button
                      type='button'
                      disabled={current === 0}
                      onClick={() => setPage(current - 1)}
                    >
                      Previous
                    </button>
                    {pageWindow(current, pages).map((p, i) =>
                      p === 'gap' ? (
                        <span key={`g${i}`} className='oa__gap'>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          type='button'
                          className={p === current ? 'is-current' : ''}
                          aria-current={p === current ? 'page' : undefined}
                          onClick={() => setPage(p)}
                        >
                          {p + 1}
                        </button>
                      )
                    )}
                    <button
                      type='button'
                      disabled={current === pages - 1}
                      onClick={() => setPage(current + 1)}
                    >
                      Next
                    </button>
                  </nav>
                )}
              </div>
            </footer>
          )}
        </div>
      </div>
    </section>
  );
};

export default EnablementTable;
